import { createTheme, ThemeProvider } from "@mui/material";
import { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./App.css";
import { ModalRoot } from "./components/modal/ModalRoot";
import CssBaseline from "@mui/material/CssBaseline";

import "./i18n/i18n";
import { Navigate, Route, useNavigate, useLocation, Routes } from "react-router";
import { GET_USER, SAVE_URL_QUERY_STATE } from "./redux/reducers/loginReducer";
import { Login } from "./components/auth/Login";
import Logout from "./components/auth/Logout";
import { LogoutInfo } from "./components/auth/LogoutInfo";
import { ErrorMessage } from "./components/error/ErrorMessage";
import {
  GET_CARE_PRINCIPAL_ID_FROM_CURRENT_USER,
  GET_CARE_UNIT_SETTINGS,
} from "./redux/reducers/administrationReducer";
import { GET_AVAILABLE_CARD_TYPES } from "./redux/reducers/cardTypeReducer";
import Shell from "./components/shell/Shell";
import { GET_SETTINGS } from "./redux/reducers/componentReducer";
import { isPrivileged } from "./util/PrivilegeUtil";
import { EMBEDDED, NATURE, SSN } from "./util/constants";
import { SessionTimeOut } from "./components/auth/SessionTimeOut";

function App() {
  const prefersDarkMode = useSelector((state: any) => state.settings.theme);
  const [mode, setMode] = useState<"light" | "dark">(prefersDarkMode);
  const session = useSelector((state: any) => state.session);
  const carePrincipal = useSelector((state: any) => state.carePrincipal);
  const settings = useSelector((state: any) => state.settings.internalSettings);
  const [redirectToContract, setRedirectToContract] = useState(false);
  const hasInitialized = useRef(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);


  const setPreferredTheme = () => {
    // const mediaQuery = "(prefers-color-scheme: dark)";
    // const mql = window.matchMedia(mediaQuery);
    // const theme = mql ? "dark" : "light";
    // dispatch({ type: "SET_THEME", theme });
  };

  useEffect(() => {
    dispatch({ type: GET_SETTINGS });
    if (location.pathname !== "/_error" &&
      location.pathname !== "/loggedout") {
      fixURLQueries();
      dispatch({ type: GET_USER, payload: {}, history: navigate });
    }
  }, [dispatch]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (session?.currentUser?.healthCareProviderHsaId && settings?.serverURL) {
      const healthCareProviderId = session.currentUser.healthCareProviderHsaId;
      dispatch({
        type: GET_CARE_PRINCIPAL_ID_FROM_CURRENT_USER,
        healthCareProviderId,
      });
      setPreferredTheme();
    }
  }, [session?.currentUser?.healthCareProviderHsaId, settings?.serverURL]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (carePrincipal.carePrincipals !== undefined) {
      const careUnitId = session.currentUser.healthCareUnitHsaId;
      const carePrincipals = carePrincipal.carePrincipals;
      dispatch({ type: GET_CARE_UNIT_SETTINGS, careUnitId, carePrincipals });
    }
  }, [carePrincipal.carePrincipals]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (session.selectedCarePrincipal !== undefined) {
      const carePrincipalId = session.selectedCarePrincipal.id;
      dispatch({ type: GET_AVAILABLE_CARD_TYPES, carePrincipalId });
    }
  }, [session.selectedCarePrincipal]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setRedirectToContract(shouldRedirectToContract());
  }, [location.pathname, carePrincipal]); //eslint-disable-line react-hooks/exhaustive-deps

  function fixURLQueries() {
    const { v4: uuidv4 } = require("uuid");
    let state = {
      id: uuidv4(),
      ssn: urlParams.get(SSN),
      nature: urlParams.get(NATURE),
      embedded: urlParams.get(EMBEDDED),
    };
    dispatch({ type: SAVE_URL_QUERY_STATE, state });
  }

  function shouldRedirectToContract() {
    return (
      !isPrivileged(session?.currentUser?.privileges, "ADMIN", "READ") &&
      (!carePrincipal.carePrincipals ||
        carePrincipal.carePrincipals?.length === 0) &&
      session.currentUser &&
      !location.pathname.includes("contract") &&
      session.currentUser.healthCareProviderHsaId
    );
  }

  const theme = useMemo(
    () =>
      createTheme({
        palette:
          mode === "dark"
            ? {
              mode: mode,
            }
            : {
              mode: mode,
              background: {
                default: "#FAFAFA",
                paper: "#ffffff",
              },
            },
      }),
    [mode]
  );

  useEffect(() => {
    setMode(prefersDarkMode);
  }, [prefersDarkMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SessionTimeOut />
      <ModalRoot />
      <Routes>
        <Route path="/login" element={session?.currentUser ? <Navigate to="/" /> : <Login />} />
        <Route path="/loggedout" element={<LogoutInfo />} />
        <Route path="/_error" element={<ErrorMessage />} />
        <Route path="/logout" element={<Logout />} />
        {redirectToContract ? (
          <Route path="*" element={<Navigate to="/contract" />} />
        ) : session?.currentUser || settings?.security === 'none' ? (
          <Route path="*" element={<Shell />} />
        ) : null}
      </Routes>
    </ThemeProvider>
  );
}
export default App;
